import {
	ChangeDetectionStrategy,
	Component,
	Inject,
	OnInit,
	PLATFORM_ID,
	signal,
	computed,
} from '@angular/core';
import {
	MatDialogRef,
	MAT_DIALOG_DATA,
	MatDialogContent,
	MatDialogTitle,
} from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconButton } from '@angular/material/button';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';

import { resendActivationMail, updateUser } from '../../../domain/user/user.actions';
import { EmailConfirmStatus, User, UserDTO } from '../../../domain/user/user.model';
import {
	selectEmailConfirmationStatus,
	selectUser,
} from '../../../domain/user/user.selectors';
import { MailConfirmComponent } from '../../mail-confirm/mail-confirm.component';
import { closeDialog } from '../dn-dialog.actions';
import { DottnetDialogBase } from '../dn-dialog.component';
import { dismissEmailConfirmation } from './mail-confirm-dialog.actions';

export interface MailConfirmDialogData {
	callbackUrl?: string;
	// Provided explicitly for the LinkExpired flow (guest user); otherwise the
	// component reads the status from the store.
	emailConfirmationStatus?: EmailConfirmStatus;
}

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	selector: 'dottnet-mat-dialog-mail-confirm',
	templateUrl: './mail-confirm-dialog.component.html',
	styleUrls: ['./mail-confirm-dialog.component.scss'],
	imports: [
		MailConfirmComponent,
		MatDialogContent,
		MatDialogTitle,
		MatIconButton,
		FaIconComponent,
	],
})
export class MailConfirmDialogComponent
	extends DottnetDialogBase<MailConfirmDialogComponent>
	implements OnInit
{
	readonly user = toSignal(
		this.store.select(selectUser).pipe(filter((user) => !!user?.idAnagrafica)),
		{ initialValue: null as UserDTO | null }
	);

	readonly emailConfirmationStatus = this.mailData.emailConfirmationStatus
		? signal(this.mailData.emailConfirmationStatus)
		: toSignal(this.store.select(selectEmailConfirmationStatus), {
				initialValue: null as EmailConfirmStatus | null,
		  });

	readonly isLocking = computed(
		() => this.emailConfirmationStatus() === EmailConfirmStatus.ShowLockingBanner
	);

	readonly EmailConfirmStatus = EmailConfirmStatus;

	dialogTitle = signal<string>('');

	constructor(
		private ts: TranslateService,
		dialogRef: MatDialogRef<MailConfirmDialogComponent>,
		@Inject(MAT_DIALOG_DATA) private mailData: MailConfirmDialogData,
		public store: Store,
		@Inject(PLATFORM_ID) platformId: Object
	) {
		super(dialogRef, store, platformId);
	}

	ngOnInit(): void {
		this.dialogTitle.set(this.ts.instant('dottnet.permessi.activateMail'));
	}

	sendMail(): void {
		this.store.dispatch(resendActivationMail({ callbackUrl: this.mailData.callbackUrl ?? '' }));
	}

	dismissEmailConfirm(): void {
		// For expired-token flow the user is a guest — nothing to persist
		// server-side, just close the dialog.
		if (this.mailData.emailConfirmationStatus === EmailConfirmStatus.LinkExpired) {
			this.store.dispatch(closeDialog());
			return;
		}
		this.store.dispatch(dismissEmailConfirmation());
	}

	onSubmitNewEmail(newEmail: string): void {
		const user = this.user();
		if (!user) return;
		const newUser: User = {
			email: newEmail,
			password: user.password,
			nome: user.nome,
			cognome: user.cognome,
			sesso: user.sesso,
			codiceFiscale: user.codiceFiscale,
			idAnagrafica: user.idAnagrafica,
			idCategoria: user.idCategoria,
			idSpecializzazione: user.idSpecializzazione,
			idComuneNascita: user.idComuneNascita,
			dataNascita: user.dataNascita,
			privacy: user.privacy,
			marketing: user.marketing,
			profilazioneTerzi: user.profilazioneTerzi,
			marketingTerzi: user.marketingTerzi,
			profilazione: user.profilazione,
			tracciamento: null,
			numeroIscrizioneAlbo: user.numeroIscrizioneAlbo?.toString(),
			provinciaIscrizioneAlbo: user.provinciaIscrizioneAlbo,
			ipAddress: '',
			cellulare: user.cellulare,
			cap: user.cap,
			idComuneEsercizio: user.idComuneEsercizio,
			idTipoProfessione: user.idTipoProfessione,
			idDisciplinaAgenas: user.idDisciplinaAgenas,
		};
		this.store.dispatch(
			updateUser({ newUser, callbackUrl: this.mailData.callbackUrl ?? '' })
		);
		this.store.dispatch(closeDialog());
	}
}
